import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Copy, Check, Send, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface InviteToPlatformModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  athleteName: string;
  athleteSport?: string;
  athleteTeam?: string;
}

const getDefaultMessage = (athleteName: string) =>
  `Hi ${athleteName},\n\nI'd like to invite you to join TheSlice — a platform connecting athletes, creators, and brands for partnership opportunities.\n\nTo sign up, visit https://theslice.ai\n\nOnce you're set up, we can explore potential deals and collaborations together.\n\nLooking forward to connecting!`;

export const InviteToPlatformModal: React.FC<InviteToPlatformModalProps> = ({
  open,
  onOpenChange,
  athleteName,
  athleteSport,
  athleteTeam,
}) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState(() => getDefaultMessage(athleteName));
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset state when athlete changes
  React.useEffect(() => {
    if (open) {
      setMessage(getDefaultMessage(athleteName));
      setRecipientEmail('');
      setCopied(false);
    }
  }, [open, athleteName]);

  const handleSendEmail = async () => {
    if (!recipientEmail.trim()) {
      toast.error('Please enter a recipient email address');
      return;
    }

    setSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          templateType: 'platform_invite',
          to: recipientEmail.trim(),
          data: {
            athleteName,
            sport: athleteSport || '',
            team: athleteTeam || '',
            personalMessage: message,
          },
        },
      });

      if (error) throw error;

      toast.success(`Invitation sent to ${recipientEmail}`);
      onOpenChange(false);
    } catch (err: any) {
      console.error('Failed to send invite:', err);
      toast.error('Failed to send invitation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success('Message copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy message');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Invite {athleteName} to TheSlice
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="email" className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email" className="flex items-center gap-1.5">
              <Send className="h-3.5 w-3.5" />
              Send Email
            </TabsTrigger>
            <TabsTrigger value="copy" className="flex items-center gap-1.5">
              <Copy className="h-3.5 w-3.5" />
              Copy for SMS
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="recipient-email">Recipient Email</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="athlete@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-message">Message</Label>
              <Textarea
                id="email-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={8}
                className="resize-none text-sm"
              />
            </div>
            <Button
              className="w-full"
              onClick={handleSendEmail}
              disabled={sending || !recipientEmail.trim()}
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </TabsContent>

          <TabsContent value="copy" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Message to Copy</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={8}
                className="resize-none text-sm"
              />
            </div>
            <Button
              className="w-full"
              variant={copied ? 'outline' : 'default'}
              onClick={handleCopyMessage}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Message
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
